/**
 * ContactLeads
 *
 * Request-info leads on the marketing contacts lists and contact modals: the
 * Lead column, the Leads filter, and the lead box on the send (or automation
 * step) the lead came from. Opening a lead shows the lead modal on the page
 * (useContactLeadModal + ContactLeadOpener); it never leaves the list.
 *
 * A contact created a lead when their message produced one: they tapped the
 * request-info link (recipient), or someone else used their link and filled
 * in the "Not you?" form (referral). These are the leads the stats pages
 * count (RequestInfoLeadCounter), so the column, the filter and the stats
 * agree. A lead the legacy CRM hard-deleted still shows, without a link.
 */
import { createContext, useCallback, useContext, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { FilterSelect } from '@/components/ui/FilterSelect';
import type { FilterSelectOption } from '@/components/ui/FilterSelect';
import { LeadDetailModal } from '@/pages/leads/LeadDetailModal';
import type { ContactLead, ContactLeadParam } from '@/services/marketing.service';
import { formatPacificDateTime } from '@/lib/facilityTimezone';
import { cn } from '@/lib/utils';

/** The Leads filter's value for "Created a lead"; the lists send it as `lead: true`. */
export const LEAD_FILTER_VALUE = 'lead';

/** The Leads filter's value as the `lead` request param. */
export function leadParam(value: string): ContactLeadParam | undefined {
  if (value === LEAD_FILTER_VALUE) return true;
  if (value === 'recipient' || value === 'referral') return value;
  return undefined;
}

/** The Leads filter's value from a `?lead=` a stats page linked with (1, recipient or referral). */
export function leadFilterFromUrl(value: string | null): string {
  if (value === 'recipient' || value === 'referral') return value;
  return value === '1' || value === 'true' ? LEAD_FILTER_VALUE : '';
}

/**
 * The Leads filter, beside each page's status filter. Its own control, so it
 * narrows whatever status is picked rather than replacing it. "Own lead" and
 * "Referral lead" are what the stats pages' two lead boxes count.
 */
export function ContactLeadFilter({ value, onChange, total, leadCount, recipientCount, referralCount }: {
  value: string;
  onChange: (value: string) => void;
  /** Every contact, for "All contacts". */
  total: number | undefined;
  /** Contacts that created a lead, either kind. */
  leadCount: number | undefined;
  /** Contacts who requested info themselves. */
  recipientCount?: number;
  /** Contacts whose link someone else used. */
  referralCount?: number;
}) {
  const options: FilterSelectOption[] = [
    { label: 'All contacts', value: '', count: total, color: '#3b82f6', textColor: '#1d4ed8' },
    { label: 'Created a lead', value: LEAD_FILTER_VALUE, count: leadCount, color: '#14b8a6', textColor: '#0f766e' },
    { label: 'Own lead', value: 'recipient', count: recipientCount, color: '#fd7e14', textColor: '#c2410c' },
    { label: 'Referral lead', value: 'referral', count: referralCount, color: '#6f42c1', textColor: '#5b21b6' },
  ];

  return <FilterSelect options={options} value={value} onChange={onChange} label="Leads" className="w-52" />;
}

const OpenLeadContext = createContext<((leadId: number) => void) | null>(null);

/** Gives every Lead pill and "View lead" inside it the page's lead modal. */
export const ContactLeadOpener = OpenLeadContext.Provider;

/**
 * The page's lead modal. `onClosed` runs when it closes, so the page can
 * refresh what the lead's actions (Spam, Trash, Convert) may have changed.
 * While it is open, the page hides its own contact modal: that overlay sits
 * above the shared modals, which the lead's own dialogs also are.
 */
export function useContactLeadModal(onClosed: () => void) {
  const [leadId, setLeadId] = useState<number | null>(null);
  const close = useCallback(() => {
    setLeadId(null);
    onClosed();
  }, [onClosed]);

  return {
    openLead: setLeadId,
    isOpen: leadId != null,
    modal: <LeadDetailModal isOpen={leadId != null} leadId={leadId} onClose={close} />,
  };
}

/**
 * Opens one lead: in the page's lead modal, or as the lead page outside a
 * ContactLeadOpener. The click stops here, so it never also opens the row's
 * contact modal.
 */
function OpenLeadButton({ lead, className, title, children }: {
  lead: ContactLead;
  className: string;
  title?: string;
  children: ReactNode;
}) {
  const openLead = useContext(OpenLeadContext);

  if (!openLead) {
    return (
      <Link to={`/leads/${lead.leadId}`} onClick={(e) => e.stopPropagation()} className={className} title={title}>
        {children}
      </Link>
    );
  }

  return (
    <button
      type="button"
      onClick={(e) => {
        e.stopPropagation();
        openLead(lead.leadId);
      }}
      className={className}
      title={title}
    >
      {children}
    </button>
  );
}

function leadLabel(lead: ContactLead): string {
  return lead.leadKind === 'referral' ? 'Referral lead' : 'Lead';
}

const PILL = 'inline-block whitespace-nowrap rounded-xl px-2.5 py-0.5 text-xs font-semibold';

/**
 * The Lead column: opens the lead, or says how many when the message produced
 * several (the contact modal lists each one).
 */
export function ContactLeadCell({ leads }: { leads: ContactLead[] | undefined }) {
  const list = leads ?? [];
  if (list.length === 0) return <span className="text-gray-400">—</span>;

  if (list.length > 1) {
    return <span className={cn(PILL, 'bg-[#ccfbf1] text-[#0f766e]')}>{list.length} leads</span>;
  }

  const lead = list[0];
  if (lead.leadDeleted) {
    return <span className={cn(PILL, 'bg-[#e9ecef] text-[#666]')}>Lead deleted</span>;
  }

  return (
    <OpenLeadButton
      lead={lead}
      className={cn(PILL, 'bg-[#ccfbf1] text-[#0f766e] hover:bg-[#99f6e4]')}
      title={lead.resourceName ? `Requested info on ${lead.resourceName}` : undefined}
    >
      {leadLabel(lead)}
    </OpenLeadButton>
  );
}

/**
 * Puts each lead on the send it came from: the last send of its step, so a
 * step that was retried shows it once. Leads whose step has no send on show
 * (a campaign lead has no step at all) come back in `unplaced`.
 */
export function placeLeadsOnSends(
  sends: { key: string; stepId: number | null | undefined }[],
  leads: ContactLead[],
): { byKey: Map<string, ContactLead[]>; unplaced: ContactLead[] } {
  const lastKeyByStep = new Map<number, string>();
  for (const s of sends) {
    if (s.stepId) lastKeyByStep.set(Number(s.stepId), s.key);
  }

  const byKey = new Map<string, ContactLead[]>();
  const unplaced: ContactLead[] = [];
  for (const lead of leads) {
    const key = lead.stepId ? lastKeyByStep.get(lead.stepId) : undefined;
    if (key === undefined) {
      unplaced.push(lead);
    } else {
      byKey.set(key, [...(byKey.get(key) ?? []), lead]);
    }
  }

  return { byKey, unplaced };
}

interface ContactLeadListProps {
  leads: ContactLead[];
  /** The viewer's zone; lead times are stored as Pacific wall-clock. */
  tz: string;
  /** Name the step on each lead, for leads shown away from their step's send. */
  showStep?: boolean;
  className?: string;
}

/** The leads one message produced, each with what was requested and a way to open the lead. */
export function ContactLeadList({ leads, tz, showStep = false, className }: ContactLeadListProps) {
  if (leads.length === 0) return null;

  return (
    <ul aria-label="Leads" className={cn('m-0 list-none space-y-1.5 p-0', className)}>
      {leads.map((lead) => (
        <li key={lead.leadId} className="rounded-md border border-[#99f6e4] bg-[#f0fdfa] px-3 py-2">
          <div className="flex items-start justify-between gap-3">
            <div className="min-w-0 text-[13px]">
              <span className="font-semibold text-[#0f766e]">
                {showStep && lead.stepOrder > 0 ? `Step ${lead.stepOrder} · ` : ''}
                {lead.leadKind === 'referral' ? 'Referral lead created' : 'Lead created'}
              </span>
              {lead.resourceName && <span className="text-[#555]"> · requested info on {lead.resourceName}</span>}
            </div>
            {lead.leadDeleted ? (
              <span className="shrink-0 text-xs text-[#999]">Lead deleted</span>
            ) : (
              <OpenLeadButton lead={lead} className="shrink-0 text-xs font-semibold text-[#00aff0] hover:underline">
                View lead
              </OpenLeadButton>
            )}
          </div>
          <div className="mt-0.5 text-xs text-[#888]">
            {formatPacificDateTime(lead.createdAt, tz)}
            {lead.leadKind === 'referral' && (
              <> · {lead.leadName || 'Someone else'} used this contact&apos;s link</>
            )}
          </div>
        </li>
      ))}
    </ul>
  );
}
